import {
  APIChartWidgetAttributes,
  APIDashboardFilterParameter,
  ArdoqId,
  DashboardFilterValues,
  HexColor,
} from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { EnhancedScopeData } from '@ardoq/data-model';
import { isEmpty } from 'lodash';
import { DashboardBuilderChartWidget } from './types';
import { DashboardModes, OnWidgetDrilldown } from '../types';
import { CommonWidgetProps } from './widgetPropTypes';
import { widgetOperations } from './operations';
import { getChartBodyProps, WidgetRenderDataProps } from './atoms/ChartBody';
import { isActiveLinkedDashboardFilter } from './utils';
import type { I18nLocale } from '@ardoq/i18n';

type GetCommonWidgetPropsParam = {
  configurationInstructions: string[] | undefined;
  dashboardId: ArdoqId | undefined;
  isLoading: boolean;
  widgetData: APIChartWidgetAttributes | DashboardBuilderChartWidget;
  userLocale: Locale;
  dashboardMode: DashboardModes;
  colors: Record<string, HexColor>;
  onDrilldown: OnWidgetDrilldown | undefined;
  isSelected: boolean;
  getComponentLinkHandler: GetComponentLinkHandler | undefined;
  getHasComponentReadPermission:
    | ((scopeData: EnhancedScopeData) => HasComponentReadPermission)
    | undefined;
  viewValidationErrors: string[];
  dashboardFilterValues: DashboardFilterValues;
  dashboardFilterParameters: APIDashboardFilterParameter[];
  confidentialFieldLabelsByName: Record<string, string>;
  i18nLocale: I18nLocale;
};

export const getCommonWidgetProps = ({
  dashboardId,
  isLoading,
  configurationInstructions,
  widgetData,
  userLocale,
  dashboardMode,
  colors,
  onDrilldown,
  getHasComponentReadPermission,
  getComponentLinkHandler,
  isSelected,
  viewValidationErrors,
  dashboardFilterParameters,
  dashboardFilterValues,
  confidentialFieldLabelsByName,
  i18nLocale,
}: GetCommonWidgetPropsParam): CommonWidgetProps => {
  const hasError = Boolean(viewValidationErrors.length);
  const isValidWidgetData =
    !isLoading &&
    !hasError &&
    isEmpty(configurationInstructions) &&
    widgetOperations.isWidgetWithData(widgetData);

  const linkedDashboardFilters =
    widgetOperations.getWidgetLinkedDashboardFilters({
      widgetData,
      globalFilterValues: dashboardFilterValues,
      dashboardFilterParameters: dashboardFilterParameters,
    });

  return {
    viewValidationErrors,
    hasWarning: 'warning' in widgetData && Boolean(widgetData.warning),
    name: widgetData.name,
    _id: widgetData._id,
    layout: widgetData.layout,
    isSelected,
    hasError,
    ...(isValidWidgetData
      ? {
          isValidWidgetData: true,
          chartBodyProps: getChartBodyProps({
            widgetRenderData: widgetData as WidgetRenderDataProps, // TODO: Fix this cast later
            userLocale,
            dashboardMode,
            colors,
            onDrilldown,
            getHasComponentReadPermission,
            getComponentLinkHandler,
            activeLinkedDashboardFilters: linkedDashboardFilters.filter(
              isActiveLinkedDashboardFilter
            ),
            dashboardId,
            selectedConfidentialFieldNames: Object.keys(
              confidentialFieldLabelsByName
            ),
            i18nLocale,
          }),
        }
      : { isValidWidgetData: false, chartBodyProps: null }),
  };
};
